// «👥 متابعات الفريق» ليومٍ واحد — كل بياع وما عمله، من غير فلاتر (قرار المالك 03/10/2026: «لما اضغط على متابعات الفريق يجبلى
// كل واحد عمل كل يوم متابعات ايه ونوعها ايه سواء بنك ارقام او جديد وكدا، بحيث ابقى عارف لو بصيت من غير ما اضغط على الفلاتر
// تحت كل واحد عمل ايه… وكذلك يبقى فيه كالندر اختار منه اليوم» — «واظهر الكلام دا عند محمد ابراهيم»).
//
// كارتٌ لكل بياع وشريك (لا جدول: لا شريط تمرير جانبي — قرار المالك 01/10/2026): المحسوب في التارجت أوّلًا، ثم المصادر
// الأربعة، ثم النتائج. ومن لم يسجّل شيئًا يظهر بصفر — الغياب عن الشغل يُرى. والضغط على الاسم يفلتر القوائم تحته.
import { useEffect, useState } from 'react';
import { ApiError, get } from '../lib/api.ts';
import { Alert, DateInput, Spinner } from '../components/ui.tsx';
import { addDays, cairoWorkDay, shortDate } from '../lib/format.ts';
import { SOURCES } from '../lib/labels.ts';

export interface TeamDayRow {
  salesUserId: string; salesName: string; role: string;
  contacts: number; counted: number; updates: number;
  leadBank: number; manual: number; webLead: number; siteChat: number;
  answered: number; noAnswer: number; unreachable: number;
  interested: number; readyToBook: number; notInterested: number; lost: number; convertedToBooking: number;
  normal: number; important: number; urgent: number;
}

/**
 * **كل رقمٍ يُضغط فينزل لعملائه** (قرار المالك 03/10/2026: «لما نضغط على حاجه فيهم سواء مهمه او تم الرد مثلا او مهتم ينزلوا على
 * قايمه العملاء دى ف اليوم وينقله الشاشه لتحت… كل نوع نضغط عليه يعمل كدا» — «ليا كأدمن ليهم كلهم… ومعاهم فى نطاق اعمالهم
 * هما فقط»): اليوم، والبياع (في الفريق)، والنوع المضغوط — والقوائم تحت تفتح عليها.
 */
export interface DayDrill {
  day: string;
  sellerId: string | null; sellerName: string | null;
  filter: { kind: 'source' | 'result' | 'status' | 'priority' | 'converted'; value: string; label: string } | null;
}

const SOURCE_KEY: Record<string, keyof TeamDayRow> = { LEAD_BANK: 'leadBank', MANUAL: 'manual', WEB_LEAD: 'webLead', SITE_CHAT: 'siteChat' };
const WEEKDAY = ['الأحد', 'الاتنين', 'التلات', 'الأربع', 'الخميس', 'الجمعة', 'السبت'];
const weekdayOf = (iso: string) => WEEKDAY[new Date(`${iso}T12:00:00Z`).getUTCDay()];

/**
 * `mine`: **يوم صاحبه وحده بنفس الكارت** (قرار المالك 03/10/2026: «خلى كل واحد من الشباب برضو يظهرله متابعاته كدا بنفس التقسيم
 * دا مش التقسيم الاول بس كل واحد يظهرله حاجته فقط») — بلا سطر الفريق، والخادم لا يُرجع غير صفّه.
 */
export function TeamDayPanel({ onDrill, mine = false }: { onDrill?: (d: DayDrill) => void; mine?: boolean }) {
  const today = cairoWorkDay();
  const [day, setDay] = useState(today);
  const [items, setItems] = useState<TeamDayRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setItems(null); setError(null);
    get<{ items: TeamDayRow[] }>(`/crm/team-day?on=${day}${mine ? '&mine=true' : ''}`)
      .then(r => { if (alive) setItems(r.items); })
      .catch(e => { if (alive) { setError(e instanceof ApiError ? e.message : 'تعذّر تحميل متابعات الفريق'); setItems([]); } });
    return () => { alive = false; };
  }, [day, mine]);

  const go = (r: TeamDayRow, filter: DayDrill['filter']) =>
    onDrill?.({ day, sellerId: mine ? null : r.salesUserId, sellerName: mine ? null : r.salesName, filter });
  const sum = (k: keyof TeamDayRow) => (items ?? []).reduce((t, r) => t + Number(r[k] ?? 0), 0);

  return (
    <section aria-label={mine ? 'متابعاتي' : 'متابعات الفريق'} className="space-y-3">
      {/* اليوم: سهمان والتقويم و«النهارده» */}
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className="btn-ghost px-3 py-1.5 text-sm" onClick={() => setDay(d => addDays(d, -1))}>→ اليوم اللي قبله</button>
        <div className="w-44"><DateInput value={day} max={today} onChange={v => { if (v) setDay(v); }} /></div>
        <button type="button" className="btn-ghost px-3 py-1.5 text-sm" disabled={day >= today}
          onClick={() => setDay(d => (addDays(d, 1) > today ? today : addDays(d, 1)))}>اليوم اللي بعده ←</button>
        {day !== today && <button type="button" className="btn-ghost px-3 py-1.5 text-sm" onClick={() => setDay(today)}>النهارده</button>}
        <span className="text-sm font-semibold text-ink-700">{weekdayOf(day)} {shortDate(day)}{day === today ? ' — النهارده' : ''}</span>
      </div>

      {error && <Alert>{error}</Alert>}
      {items === null ? <Spinner /> : (
        <>
          {/* إجمالي الفريق في سطر */}
          {!mine && <div className="flex flex-wrap gap-2 rounded-xl bg-brand-50 px-3 py-2 text-sm text-brand-900">
            <b>الفريق كله:</b>
            <span>اتحسبت في التارجت <b className="tabular-nums">{sum('counted')}</b></span>·
            <span>كل التواصلات <b className="tabular-nums">{sum('contacts')}</b></span>·
            {SOURCES.map(s => <span key={s.code}>{s.icon} {s.ar} <b className="tabular-nums">{sum(SOURCE_KEY[s.code])}</b></span>)}·
            <span>حجوزات <b className="tabular-nums">{sum('convertedToBooking')}</b></span>
          </div>}

          <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {items.map(r => {
              const idle = r.contacts === 0;
              return (
                <li key={r.salesUserId} className={`card p-3 ${idle ? 'opacity-70' : ''}`}>
                  <div className="flex items-start justify-between gap-2">
                    <button type="button" className="text-start text-base font-bold text-brand-800 hover:underline"
                      aria-label={`متابعات ${r.salesName}`} onClick={() => go(r, null)}>
                      {r.salesName}
                    </button>
                    <button type="button" className="text-center leading-tight" aria-label={`اتحسبت في التارجت ${r.counted}`} onClick={() => go(r, null)}>
                      <div className={`text-2xl font-bold tabular-nums ${idle ? 'text-ink-400' : 'text-emerald-700'}`}>{r.counted}</div>
                      <div className="text-[11px] text-ink-500">اتحسبت في التارجت</div>
                    </button>
                  </div>
                  {idle ? (
                    <p className="mt-2 text-sm text-ink-500">{mine ? 'ما سجّلتش متابعات في اليوم ده.' : 'ما سجّلش متابعات في اليوم ده.'}</p>
                  ) : (
                    <>
                      <button type="button" className="mt-1 text-start text-xs text-ink-600 hover:underline" onClick={() => go(r, null)}>
                        كل التواصلات <b className="tabular-nums">{r.contacts}</b> — منها تحديث موقف <b className="tabular-nums">{r.updates}</b>
                      </button>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {SOURCES.map(src => (
                          <Chip key={src.code} n={Number(r[SOURCE_KEY[src.code]])} tone={src.tone} text={`${src.icon} ${src.ar}`}
                            onClick={() => go(r, { kind: 'source', value: src.code, label: src.ar })} />
                        ))}
                      </div>
                      <div className="mt-1.5 flex flex-wrap gap-1.5" aria-label="الأولوية">
                        {PRIORITY_CHIPS.map(p => (
                          <Chip key={p.code} n={Number(r[p.key])} tone={p.tone} text={`${p.icon} ${p.ar}`}
                            onClick={() => go(r, { kind: 'priority', value: p.code, label: p.ar })} />
                        ))}
                      </div>
                      <div className="mt-2 grid grid-cols-4 gap-1 text-center text-xs">
                        {MINIS.map(m => (
                          <Mini key={m.label} label={m.label} n={Number(r[m.key])} tone={m.tone} onClick={() => go(r, m.filter)} />
                        ))}
                      </div>
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </section>
  );
}

// الأولوية (المالك 03/10/2026: «عاوزك تزود الاولوية مهمه او عاجله»)
const PRIORITY_CHIPS: { code: string; ar: string; icon: string; key: keyof TeamDayRow; tone: string }[] = [
  { code: 'NORMAL', ar: 'عادية', icon: '⚪', key: 'normal', tone: 'bg-slate-100 text-ink-700' },
  { code: 'IMPORTANT', ar: 'مهمة', icon: '🟠', key: 'important', tone: 'bg-amber-100 text-amber-800' },
  { code: 'URGENT', ar: 'عاجلة', icon: '🔴', key: 'urgent', tone: 'bg-rose-100 text-rose-800' },
];
const MINIS: { label: string; key: keyof TeamDayRow; tone?: string; filter: DayDrill['filter'] }[] = [
  { label: 'تم الرد', key: 'answered', filter: { kind: 'result', value: 'ANSWERED', label: 'تم الرد' } },
  { label: 'لم يرد', key: 'noAnswer', filter: { kind: 'result', value: 'NO_ANSWER', label: 'لم يرد' } },
  { label: 'مغلق', key: 'unreachable', filter: { kind: 'result', value: 'UNREACHABLE', label: 'الرقم مغلق أو غير متاح' } },
  { label: 'مهتم', key: 'interested', filter: { kind: 'status', value: 'INTERESTED', label: 'مهتم' } },
  { label: 'جاهز للحجز', key: 'readyToBook', filter: { kind: 'status', value: 'READY_TO_BOOK', label: 'جاهز للحجز' } },
  { label: 'حجوزات', key: 'convertedToBooking', tone: 'text-emerald-700', filter: { kind: 'converted', value: 'true', label: 'اتحوّلت لحجز' } },
  { label: 'غير مهتم', key: 'notInterested', tone: 'text-rose-700', filter: { kind: 'status', value: 'NOT_INTERESTED', label: 'غير مهتم' } },
  { label: 'فقدناه', key: 'lost', tone: 'text-rose-700', filter: { kind: 'status', value: 'LOST', label: 'فقدنا العميل' } },
];

function Chip({ n, tone, text, onClick }: { n: number; tone: string; text: string; onClick: () => void }) {
  return (
    <button type="button" disabled={!n} onClick={onClick}
      className={`rounded-full px-2 py-0.5 text-xs font-semibold ${n ? `${tone} hover:ring-2 hover:ring-brand-300` : 'bg-slate-50 text-ink-400'}`}>
      {text} {n}
    </button>
  );
}

function Mini({ label, n, tone, onClick }: { label: string; n: number; tone?: string; onClick: () => void }) {
  return (
    <button type="button" disabled={!n} onClick={onClick} aria-label={`${label} ${n}`}
      className={`rounded-lg bg-slate-50 px-1 py-1 ${n ? 'hover:bg-brand-50 hover:ring-1 hover:ring-brand-300' : ''}`}>
      <div className={`text-sm font-bold tabular-nums ${n ? (tone ?? 'text-ink-900') : 'text-ink-400'}`}>{n}</div>
      <div className="text-[11px] text-ink-500">{label}</div>
    </button>
  );
}
